<script lang="ts" setup>
import {
  isYandexMapReadyToInit,
  initYmaps,
  YandexMap,
  YandexMapClusterer,
  YandexMapControls,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultSchemeLayer,
  YandexMapGeolocationControl,
  YandexMapListener,
  YandexMapZoomControl,
  yandexMapLoadError,
  yandexMapLoadStatus,
  yandexMapScript,
} from 'vue-yandex-maps';
import { debounce } from '@antfu/utils';
import clustererUrl from '@yandex/ymaps3-clusterer/dist/index.js?url';
import controlsUrl from '@yandex/ymaps3-default-ui-theme/dist/index.js?url';
import { preparePickupMapModules } from '~/utils/preparePickupMapModules';
import type { Location, PickupPoint } from '~/types/Checkout';
import type { YMapLocation } from '@yandex/ymaps3-types/imperative/YMap';
import type { MapEventUpdateHandler, YMap } from '@yandex/ymaps3-types';
import type { YMapClusterer } from '@yandex/ymaps3-clusterer';
import { isValidPickupPoint } from '~/utils/checkoutDelivery';

const props = defineProps<{ points: PickupPoint[]; selectedLocation: Location }>();

const map = shallowRef<YMap | null>(null);
const clusterer = shallowRef<YMapClusterer | undefined>(undefined);
const localLocation = shallowRef<YMapLocation | null>(null);
const mapRenderKey = ref(0);
const areMapModulesReady = ref(false);
const mapModulesError = shallowRef<unknown>(null);
const canRenderYandexMap = computed(
  () => isYandexMapReadyToInit.value && !yandexMapLoadError.value && areMapModulesReady.value,
);
const isYandexMapLoading = computed(
  () =>
    isYandexMapReadyToInit.value &&
    !yandexMapLoadError.value &&
    !mapModulesError.value &&
    (!areMapModulesReady.value || map.value === null),
);
let preparationId = 0;

async function prepareMap() {
  if (!isYandexMapReadyToInit.value) return;

  const id = ++preparationId;
  areMapModulesReady.value = false;
  mapModulesError.value = null;
  try {
    await initYmaps();
    await preparePickupMapModules(ymaps3, { clusterer: clustererUrl, controls: controlsUrl });
    if (id === preparationId) areMapModulesReady.value = true;
  } catch (error) {
    if (id !== preparationId) return;
    mapModulesError.value = error;
    console.error('[PickupPointsMap] Failed to prepare map modules', error);
  }
}

onMounted(prepareMap);
onBeforeUnmount(() => {
  preparationId++;
});

const checkoutStore = useCheckoutStore();
const { openMarker } = storeToRefs(checkoutStore);
const validPoints = computed(() => props.points.filter(isValidPickupPoint));

const defaultCenter = computed<[lon: number, lat: number]>(() =>
  props.selectedLocation
    ? [Number(props.selectedLocation.data.geo_lon), Number(props.selectedLocation.data.geo_lat)]
    : [37.6142, 55.7498],
);
const currentZoom = ref(11);
const duration = 500;

watch(
  () => props.selectedLocation,
  () => {
    currentZoom.value = 11;
    localLocation.value = null;
    openMarker.value = null;
  },
);

watch(
  () => openMarker.value,
  () => {
    if (openMarker.value !== null) {
      currentZoom.value = currentZoom.value > 16 ? currentZoom.value : 16;
      localLocation.value = {
        center: [Number(openMarker.value.coord_x), Number(openMarker.value.coord_y)],
        zoom: currentZoom.value,
        duration,
      } as typeof localLocation.value;
    }
  },
);

const handleMapUpdate: MapEventUpdateHandler = (object) => {
  if (object.location.zoom !== currentZoom.value) {
    localLocation.value = object.location;
    currentZoom.value = object.location.zoom;
  }
};

const debouncedMapUpdateHandle = debounce(500, handleMapUpdate);

function getYandexMapLoadErrorReason(error: unknown) {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error !== null && 'type' in error && typeof error.type === 'string') {
    return `script event: ${error.type}`;
  }

  return 'unknown error';
}

watch(yandexMapLoadError, (error) => {
  if (!error) return;

  console.error(`[PickupPointsMap] Yandex Maps failed to load: ${getYandexMapLoadErrorReason(error)}`);
});

function retryYandexMapLoad() {
  if (!import.meta.client || !isYandexMapReadyToInit.value) return;

  if (yandexMapLoadError.value || mapModulesError.value) {
    yandexMapScript.value?.remove();
    yandexMapScript.value = null;
    yandexMapLoadError.value = null;
    yandexMapLoadStatus.value = 'pending';

    for (const key of Object.keys(window)) {
      if (key.includes('ymaps3')) Reflect.deleteProperty(window, key);
    }
  }

  map.value = null;
  clusterer.value = undefined;
  mapRenderKey.value += 1;
  prepareMap();
}
</script>

<template>
  <div class="relative min-h-[360px] flex-grow overflow-hidden rounded-xl">
    <YandexMap
      v-if="canRenderYandexMap"
      :key="mapRenderKey"
      v-model="map"
      :settings="{
        location: localLocation || {
          center: defaultCenter,
          zoom: currentZoom,
        },
        behaviors: ['drag', 'pinchZoom', 'dblClick', 'oneFingerZoom'],
      }"
      class="absolute inset-0 size-full transition-opacity"
      :class="{ 'opacity-0': isYandexMapLoading }"
      real-settings-location
    >
      <YandexMapDefaultSchemeLayer />
      <YandexMapDefaultFeaturesLayer />
      <YandexMapListener :settings="{ onUpdate: debouncedMapUpdateHandle }" />

      <YandexMapControls :settings="{ position: 'right' }">
        <YandexMapZoomControl />
        <YandexMapGeolocationControl />
      </YandexMapControls>

      <YandexMapClusterer
        v-if="currentZoom < 17"
        v-model="clusterer"
        :grid-size="currentZoom <= 14 ? 64 : 32"
        zoom-on-cluster-click
      >
        <template #default>
          <PickupPointItemOnMap
            v-for="point in validPoints"
            :key="point.code"
            :is-show-price="currentZoom > 13"
            :pickup-point="point"
          />
        </template>

        <template #cluster="{ length }">
          <div class="ym-cluster">
            {{ length }}
          </div>
        </template>
      </YandexMapClusterer>

      <template v-else>
        <PickupPointItemOnMap
          v-for="point in validPoints"
          :key="point.code"
          :is-show-price="currentZoom > 13"
          :pickup-point="point"
        />
      </template>
    </YandexMap>

    <div
      v-if="isYandexMapLoading"
      class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/5 px-4 text-center text-black/60"
    >
      <SvgoSpinner
        :font-controlled="false"
        class="animate-spin size-8"
      />

      <p>Загружаем карту</p>
    </div>

    <div
      v-else-if="!canRenderYandexMap"
      class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/5 px-4 text-center text-black/40"
    >
      <p>Карта временно недоступна</p>

      <TheBtn
        v-if="isYandexMapReadyToInit"
        color="outline"
        size="md"
        @click="retryYandexMapLoad"
      >
        Повторить
      </TheBtn>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ym-cluster {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  border: 3px solid $white;
  border-radius: 100%;
  background-color: $black;
  color: $white;
  box-shadow: 0 4px 4px 0 rgb(0 0 0 / 25%);
  font-weight: $bold;
  cursor: pointer;
}
</style>
